import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { pricingAPI } from '@/services/api';

// Types for pricing configuration
export interface BasePrice {
  singlePhoto: number; // Price in cents
  smallBundle: number; // 5 photos
  mediumBundle: number; // 10 photos
  largeBundle: number; // 20 photos
  fullEvent: number; // All photos
}

export interface VolumeDiscount {
  photoCount: number;
  discountPercent: number;
}

export interface EventTypeMultiplier {
  eventType: string;
  multiplier: number;
  displayName: string;
}

export interface SpecialOffer {
  id: string;
  name: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  conditions?: {
    minPhotos?: number;
    maxPhotos?: number;
    eventTypes?: string[];
    timeLimit?: number; // Hours after event
  };
  isActive: boolean;
  validUntil?: string;
}

export interface PricingConfiguration {
  basePrices: BasePrice;
  volumeDiscounts: VolumeDiscount[];
  eventTypeMultipliers: EventTypeMultiplier[];
  specialOffers: SpecialOffer[];
  photographerCommission: number; // Percentage (0.7 = 70%)
  updatedAt?: string;
}

export interface PricingPreview {
  eventType: string;
  photoCount: number;
  basePrice: number;
  volumeDiscount: number;
  eventMultiplier: number;
  specialDiscount: number;
  finalPrice: number;
  photographerEarning: number;
}

interface PricingState {
  configuration: PricingConfiguration | null;
  loading: boolean;
  error: string | null;
  preview: PricingPreview | null;
}

type PricingAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: PricingConfiguration }
  | { type: 'FETCH_FAILURE'; payload: string }
  | { type: 'UPDATE_BASE_PRICES'; payload: BasePrice }
  | { type: 'UPDATE_VOLUME_DISCOUNTS'; payload: VolumeDiscount[] }
  | { type: 'UPDATE_EVENT_MULTIPLIERS'; payload: EventTypeMultiplier[] }
  | { type: 'UPDATE_SPECIAL_OFFERS'; payload: SpecialOffer[] }
  | { type: 'UPDATE_COMMISSION'; payload: number }
  | {
      type: 'CALCULATE_PREVIEW';
      payload: { eventType: string; photoCount: number; promoCode?: string };
    }
  | { type: 'CLEAR_PREVIEW' }
  | { type: 'CLEAR_ERROR' };

const initialState: PricingState = {
  configuration: null,
  loading: false,
  error: null,
  preview: null,
};

// Default pricing configuration based on STRIPE_PRODUCT_STRATEGY.md
const defaultConfiguration: PricingConfiguration = {
  basePrices: {
    singlePhoto: 500, // $5.00
    smallBundle: 1500, // $15.00 for 5 photos
    mediumBundle: 2500, // $25.00 for 10 photos
    largeBundle: 4000, // $40.00 for 20 photos
    fullEvent: 7500, // $75.00 for all photos
  },
  volumeDiscounts: [
    { photoCount: 5, discountPercent: 10 },
    { photoCount: 10, discountPercent: 15 },
    { photoCount: 20, discountPercent: 20 },
    { photoCount: 50, discountPercent: 25 },
  ],
  eventTypeMultipliers: [
    { eventType: 'wedding', multiplier: 1.5, displayName: 'Wedding' },
    { eventType: 'corporate', multiplier: 1.3, displayName: 'Corporate' },
    { eventType: 'birthday', multiplier: 1.0, displayName: 'Birthday' },
    { eventType: 'sports', multiplier: 0.9, displayName: 'Sports' },
  ],
  specialOffers: [],
  photographerCommission: 0.7, // 70%
};

const pricingReducer = (state: PricingState, action: PricingAction): PricingState => {
  switch (action.type) {
    case 'FETCH_START':
      return {
        ...state,
        loading: true,
        error: null,
      };

    case 'FETCH_SUCCESS':
      return {
        ...state,
        loading: false,
        configuration: action.payload,
        error: null,
      };

    case 'FETCH_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case 'UPDATE_BASE_PRICES':
      return {
        ...state,
        configuration: state.configuration
          ? {
              ...state.configuration,
              basePrices: action.payload,
              updatedAt: new Date().toISOString(),
            }
          : null,
      };

    case 'UPDATE_VOLUME_DISCOUNTS':
      return {
        ...state,
        configuration: state.configuration
          ? {
              ...state.configuration,
              volumeDiscounts: action.payload,
              updatedAt: new Date().toISOString(),
            }
          : null,
      };

    case 'UPDATE_EVENT_MULTIPLIERS':
      return {
        ...state,
        configuration: state.configuration
          ? {
              ...state.configuration,
              eventTypeMultipliers: action.payload,
              updatedAt: new Date().toISOString(),
            }
          : null,
      };

    case 'UPDATE_SPECIAL_OFFERS':
      return {
        ...state,
        configuration: state.configuration
          ? {
              ...state.configuration,
              specialOffers: action.payload,
              updatedAt: new Date().toISOString(),
            }
          : null,
      };

    case 'UPDATE_COMMISSION':
      return {
        ...state,
        configuration: state.configuration
          ? {
              ...state.configuration,
              photographerCommission: action.payload,
              updatedAt: new Date().toISOString(),
            }
          : null,
      };

    case 'CALCULATE_PREVIEW': {
      if (!state.configuration) return state;

      const { eventType, photoCount, promoCode } = action.payload;
      const config = state.configuration;

      // Calculate base price (simplified logic)
      let basePrice: number;
      if (photoCount === 1) {
        basePrice = config.basePrices.singlePhoto;
      } else if (photoCount <= 5) {
        basePrice = config.basePrices.smallBundle;
      } else if (photoCount <= 10) {
        basePrice = config.basePrices.mediumBundle;
      } else if (photoCount <= 20) {
        basePrice = config.basePrices.largeBundle;
      } else {
        basePrice = config.basePrices.fullEvent;
      }

      // Apply event type multiplier
      const multiplier =
        config.eventTypeMultipliers.find(m => m.eventType === eventType)?.multiplier || 1.0;
      const multipliedPrice = Math.round(basePrice * multiplier);

      // Apply volume discount
      const volumeDiscount = config.volumeDiscounts
        .filter(d => photoCount >= d.photoCount)
        .sort((a, b) => b.discountPercent - a.discountPercent)[0];

      const volumeDiscountAmount = volumeDiscount
        ? Math.round((multipliedPrice * volumeDiscount.discountPercent) / 100)
        : 0;

      // Apply special offers (simplified)
      let specialDiscountAmount = 0;
      if (promoCode) {
        const offer = config.specialOffers.find(
          o => o.isActive && o.name.toLowerCase() === promoCode.toLowerCase()
        );
        if (offer) {
          specialDiscountAmount =
            offer.discountType === 'percentage'
              ? Math.round(((multipliedPrice - volumeDiscountAmount) * offer.discountValue) / 100)
              : offer.discountValue;
        }
      }

      const finalPrice = multipliedPrice - volumeDiscountAmount - specialDiscountAmount;
      const photographerEarning = Math.round(finalPrice * config.photographerCommission);

      const preview: PricingPreview = {
        eventType,
        photoCount,
        basePrice: basePrice,
        volumeDiscount: volumeDiscountAmount,
        eventMultiplier: multipliedPrice - basePrice,
        specialDiscount: specialDiscountAmount,
        finalPrice,
        photographerEarning,
      };

      return {
        ...state,
        preview,
      };
    }

    case 'CLEAR_PREVIEW':
      return {
        ...state,
        preview: null,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

interface PricingContextType {
  configuration: PricingConfiguration | null;
  loading: boolean;
  error: string | null;
  preview: PricingPreview | null;
  fetchConfiguration: () => Promise<void>;
  updateBasePrices: (prices: BasePrice) => Promise<void>;
  updateVolumeDiscounts: (discounts: VolumeDiscount[]) => Promise<void>;
  updateEventMultipliers: (multipliers: EventTypeMultiplier[]) => Promise<void>;
  updateSpecialOffers: (offers: SpecialOffer[]) => Promise<void>;
  updateCommission: (commission: number) => Promise<void>;
  calculatePreview: (eventType: string, photoCount: number, promoCode?: string) => void;
  clearPreview: () => void;
  clearError: () => void;
}

const PricingContext = createContext<PricingContextType | null>(null);

interface PricingProviderProps {
  children: ReactNode;
}

export const PricingProvider: React.FC<PricingProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(pricingReducer, initialState);

  const fetchConfiguration = async (): Promise<void> => {
    dispatch({ type: 'FETCH_START' });

    try {
      const response = await pricingAPI.getConfiguration();
      const config: PricingConfiguration = {
        basePrices: response.data.basePrices,
        volumeDiscounts: response.data.volumeDiscounts,
        eventTypeMultipliers: response.data.eventTypeMultipliers,
        specialOffers: response.data.specialOffers || [],
        photographerCommission: response.data.photographerCommission,
        updatedAt: response.data.updatedAt,
      };
      dispatch({ type: 'FETCH_SUCCESS', payload: config });
    } catch {
      // Fallback to default configuration on error
      dispatch({ type: 'FETCH_SUCCESS', payload: defaultConfiguration });
    }
  };

  const updateBasePrices = async (prices: BasePrice): Promise<void> => {
    try {
      await pricingAPI.updateBasePrices(prices);
      dispatch({ type: 'UPDATE_BASE_PRICES', payload: prices });
    } catch (error: any) {
      dispatch({
        type: 'FETCH_FAILURE',
        payload: error.message || 'Failed to update base prices',
      });
      throw error;
    }
  };

  const updateVolumeDiscounts = async (discounts: VolumeDiscount[]): Promise<void> => {
    try {
      await pricingAPI.updateVolumeDiscounts(discounts);
      dispatch({ type: 'UPDATE_VOLUME_DISCOUNTS', payload: discounts });
    } catch (error: any) {
      dispatch({
        type: 'FETCH_FAILURE',
        payload: error.message || 'Failed to update volume discounts',
      });
      throw error;
    }
  };

  const updateEventMultipliers = async (multipliers: EventTypeMultiplier[]): Promise<void> => {
    try {
      await pricingAPI.updateEventMultipliers(multipliers);
      dispatch({ type: 'UPDATE_EVENT_MULTIPLIERS', payload: multipliers });
    } catch (error: any) {
      dispatch({
        type: 'FETCH_FAILURE',
        payload: error.message || 'Failed to update event multipliers',
      });
      throw error;
    }
  };

  const updateSpecialOffers = async (offers: SpecialOffer[]): Promise<void> => {
    try {
      await pricingAPI.updateSpecialOffers(offers);
      dispatch({ type: 'UPDATE_SPECIAL_OFFERS', payload: offers });
    } catch (error: any) {
      dispatch({
        type: 'FETCH_FAILURE',
        payload: error.message || 'Failed to update special offers',
      });
      throw error;
    }
  };

  const updateCommission = async (commission: number): Promise<void> => {
    try {
      await pricingAPI.updateCommission(commission);
      dispatch({ type: 'UPDATE_COMMISSION', payload: commission });
    } catch (error: any) {
      dispatch({
        type: 'FETCH_FAILURE',
        payload: error.message || 'Failed to update commission',
      });
      throw error;
    }
  };

  const calculatePreview = (eventType: string, photoCount: number, promoCode?: string): void => {
    dispatch({
      type: 'CALCULATE_PREVIEW',
      payload: { eventType, photoCount, promoCode },
    });
  };

  const clearPreview = (): void => {
    dispatch({ type: 'CLEAR_PREVIEW' });
  };

  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value: PricingContextType = {
    ...state,
    fetchConfiguration,
    updateBasePrices,
    updateVolumeDiscounts,
    updateEventMultipliers,
    updateSpecialOffers,
    updateCommission,
    calculatePreview,
    clearPreview,
    clearError,
  };

  return <PricingContext.Provider value={value}>{children}</PricingContext.Provider>;
};

export const usePricing = (): PricingContextType => {
  const context = useContext(PricingContext);
  if (!context) {
    throw new Error('usePricing must be used within PricingProvider');
  }
  return context;
};
